/**
 * imageAdjust — réglages non destructifs d'un calque image.
 *
 * Cinq familles, celles qu'on attend d'un inspecteur : flou gaussien,
 * luminosité, contraste, niveaux, teinte et saturation. Tout vit dans
 * `el.adjust` ; l'image source n'est jamais touchée, comme la gomme (cf.
 * eraseMask.js) — on peut revenir en arrière indéfiniment.
 *
 * DEUX RENDUS, UNE SEULE VÉRITÉ
 *  - À l'écran : un `filter` CSS sur la balise image (cf. ImageLayer). Le GPU
 *    s'en charge, les curseurs restent fluides sur une image de 4000 px.
 *  - À l'export : les réglages sont CUITS dans les pixels avant l'envoi, sur
 *    TOUS les chemins. Le service de rendu passe par Chromium et saurait lire
 *    le `filter`, mais c'est déjà la règle de la maison pour la gomme (cf.
 *    api/client.js) : aplatir ici met le chemin SVG, le chemin WeasyPrint et le
 *    chemin Chromium sur le même pied, et supprime toute question sur la façon
 *    dont `zoom` compose avec un rayon de flou.
 *
 * LE FLOU SE MESURE EN MILLIMÈTRES, et il est ROGNÉ AU CADRE.
 * En millimètres parce que c'est l'unité du document, comme le rayon de la
 * gomme : un flou de 2 mm reste un flou de 2 mm à l'impression, quel que soit le
 * zoom d'édition. Rogné au cadre parce que l'export cuit l'image dans un canvas
 * à la forme du CADRE : laisser le flou déborder à l'écran donnerait deux
 * résultats différents selon le chemin de rendu, ce qui est exactement le genre
 * d'écart qu'on ne découvre qu'une fois le PDF chez l'imprimeur.
 *
 * LES NIVEAUX NE SONT PAS UN FILTRE CSS.
 * Point noir, point blanc et gamma n'existent pas dans la liste des fonctions
 * `filter`. Ils passent par un filtre SVG `feComponentTransfer` — deux passes,
 * une affine puis une gamma — référencé par `filter: url(#…)`. Le filtre est
 * déclaré DANS ImageLayer, à côté de l'image : il voyage ainsi de lui-même vers
 * l'export Chromium, qui monte le même composant.
 */

/** Valeurs neutres. Un réglage absent vaut sa valeur neutre — jamais 0. */
export const REGLAGES_NEUTRES = {
    blur: 0,          // millimètres
    brightness: 100,  // pourcentage, 100 = inchangé
    contrast: 100,    // pourcentage
    saturation: 100,  // pourcentage
    hue: 0,           // degrés
    levelsBlack: 0,   // 0..254, entrée noire
    levelsWhite: 255, // 1..255, entrée blanche
    levelsGamma: 1,   // 0,1..5 — > 1 éclaircit les tons moyens
};

/** Réglages d'un calque, complétés par les valeurs neutres. */
export function reglagesDe(el) {
    return { ...REGLAGES_NEUTRES, ...(el && el.adjust ? el.adjust : {}) };
}

/** Le calque porte-t-il un réglage qui change quelque chose ? */
export function aDesReglages(el) {
    if (!el || el.type !== 'image' || !el.adjust) return false;
    const r = reglagesDe(el);
    return Object.keys(REGLAGES_NEUTRES).some((k) => Math.abs(r[k] - REGLAGES_NEUTRES[k]) > 1e-6);
}

/** Les niveaux sont-ils sortis de leur position neutre ? */
export function niveauxActifs(el) {
    const r = reglagesDe(el);
    return r.levelsBlack > 0 || r.levelsWhite < 255 || Math.abs(r.levelsGamma - 1) > 1e-6;
}

/** Identifiant du filtre SVG des niveaux, propre au calque. */
export function idNiveaux(el) {
    return `lf-niv-${String(el?.id || 'x').replace(/[^\w-]/g, '')}`;
}

/**
 * Coefficients des deux passes de niveaux.
 * Passe 1, affine : `out = pente × in + decalage`, ce qui étire [noir, blanc]
 * sur [0, 1] (les débordements sont écrêtés par le filtre, c'est le
 * comportement attendu d'un point noir et d'un point blanc).
 * Passe 2, gamma : `out = in ^ exposant`, avec `exposant = 1 / gamma` pour que
 * gamma > 1 ÉCLAIRCISSE, comme dans les logiciels de retouche.
 */
export function paramsNiveaux(el) {
    const r = reglagesDe(el);
    const noir = Math.min(254, Math.max(0, r.levelsBlack)) / 255;
    const blanc = Math.max(noir + 1 / 255, Math.min(255, r.levelsWhite) / 255);
    const pente = 1 / (blanc - noir);
    return {
        pente,
        decalage: -noir * pente,
        exposant: 1 / Math.max(0.01, r.levelsGamma),
    };
}

/**
 * Chaîne `filter` CSS des réglages, hors niveaux.
 *
 * L'ORDRE N'EST PAS DÉCORATIF : les fonctions s'appliquent de gauche à droite.
 * Les niveaux d'abord (remise à plat des tons bruts), puis luminosité et
 * contraste, puis teinte et saturation, et le flou EN DERNIER — flouter avant
 * d'augmenter le contraste ferait ressortir le grain qu'on venait d'adoucir.
 *
 * @param {object} el calque
 * @param {boolean} avecNiveaux inclure la référence au filtre SVG des niveaux
 */
export function chaineFiltre(el, avecNiveaux = true) {
    if (!aDesReglages(el)) return '';
    const r = reglagesDe(el);
    const bouts = [];
    if (avecNiveaux && niveauxActifs(el)) bouts.push(`url(#${idNiveaux(el)})`);
    if (r.brightness !== 100) bouts.push(`brightness(${r.brightness / 100})`);
    if (r.contrast !== 100) bouts.push(`contrast(${r.contrast / 100})`);
    if (r.saturation !== 100) bouts.push(`saturate(${r.saturation / 100})`);
    if (r.hue !== 0) bouts.push(`hue-rotate(${r.hue}deg)`);
    // Le rayon est en millimètres du document ; la boîte CSS du calque mesure
    // exactement `el.width` unités pour `el.width` millimètres. Un `px` de cette
    // boîte vaut donc un millimètre : la conversion est l'identité.
    if (r.blur > 0) bouts.push(`blur(${r.blur}px)`);
    return bouts.join(' ');
}

/**
 * Table de correspondance 0..255 des niveaux, pour la cuisson à l'export.
 * Reprend exactement les deux passes de `paramsNiveaux` : c'est la même
 * arithmétique que celle du filtre SVG, écrite une seconde fois parce qu'un
 * canvas ne sait pas exécuter un `feComponentTransfer`. Les deux doivent rester
 * en phase — toute correction ici en appelle une là-bas.
 */
export function tableNiveaux(el) {
    const { pente, decalage, exposant } = paramsNiveaux(el);
    const t = new Uint8ClampedArray(256);
    for (let i = 0; i < 256; i++) {
        const lineaire = (i / 255) * pente + decalage;
        const borne = Math.min(1, Math.max(0, lineaire));
        t[i] = Math.round(Math.pow(borne, exposant) * 255);
    }
    return t;
}

/**
 * Cuit les réglages d'un calque image dans un PNG, au format du CADRE.
 *
 * Même géométrie que `composeErased` (cf. eraseMask.js) : canvas au ratio de la
 * boîte, image cadrée « contain » — le rendu est donc celui de l'écran, au
 * pixel près, gomme comprise si elle est déjà appliquée.
 *
 * @returns {Promise<string|null>} data-URL, ou null si l'image est illisible
 */
export async function cuireReglages(el) {
    const { loadImage } = await import('./eraseMask');
    let img;
    try {
        img = await loadImage(el.url);
    } catch (_) {
        return null;
    }

    const boxW = Math.max(0.01, el.width || 1);
    const boxH = Math.max(0.01, el.height || 1);
    const ratio = boxW / boxH;
    let ch = img.naturalHeight || img.height || 1;
    let cw = ch * ratio;
    if (cw < (img.naturalWidth || img.width)) {
        cw = img.naturalWidth || img.width;
        ch = cw / ratio;
    }
    const k = Math.min(1, 4000 / Math.max(cw, ch));
    cw = Math.max(2, Math.round(cw * k));
    ch = Math.max(2, Math.round(ch * k));

    const faireCanvas = () => {
        const c = document.createElement('canvas');
        c.width = cw;
        c.height = ch;
        return c;
    };

    // ── Passe 1 : l'image nue, cadrée « contain » comme à l'écran. ─────────
    const cvs = faireCanvas();
    const ctx = cvs.getContext('2d', { willReadFrequently: true });
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const s = Math.min(cw / iw, ch / ih);
    // Un calque étiré (cartouche de l'agent, `etirer`) remplit son cadre, comme à l'écran.
    if (el.etirer) ctx.drawImage(img, 0, 0, cw, ch);
    else ctx.drawImage(img, (cw - iw * s) / 2, (ch - ih * s) / 2, iw * s, ih * s);

    // ── Passe 2 : les NIVEAUX, et ils passent EN PREMIER. ─────────────────
    //
    // L'ordre n'est pas un détail de mise en œuvre, c'est le résultat. À l'écran
    // la chaîne `filter` commence par le filtre SVG des niveaux, puis enchaîne
    // luminosité, contraste, saturation, teinte, flou. Cuire dans l'autre sens
    // donne une AUTRE image : mesuré au banc, 7,34/255 d'écart moyen et 31 de
    // pointe sur un calque portant les deux familles de réglages à la fois.
    // Le défaut était invisible réglage par réglage — chaque cas isolé tombait à
    // zéro d'écart — et n'apparaissait que sur le mélange, c'est-à-dire sur le
    // seul cas que l'utilisateur produit vraiment.
    if (niveauxActifs(el)) {
        const t = tableNiveaux(el);
        let px;
        try {
            px = ctx.getImageData(0, 0, cw, ch);
        } catch (_) {
            return null; // canvas taché (image distante sans CORS)
        }
        const d = px.data;
        // L'alpha (i+3) reste intact : les niveaux touchent la couleur, pas la
        // découpe. Une image détourée garde son fond transparent.
        for (let i = 0; i < d.length; i += 4) {
            d[i] = t[d[i]];
            d[i + 1] = t[d[i + 1]];
            d[i + 2] = t[d[i + 2]];
        }
        ctx.putImageData(px, 0, 0);
    }

    // ── Passe 3 : les fonctions `filter`, dans l'ordre de la chaîne CSS. ───
    const r = reglagesDe(el);
    const filtres = [];
    if (r.brightness !== 100) filtres.push(`brightness(${r.brightness / 100})`);
    if (r.contrast !== 100) filtres.push(`contrast(${r.contrast / 100})`);
    if (r.saturation !== 100) filtres.push(`saturate(${r.saturation / 100})`);
    if (r.hue !== 0) filtres.push(`hue-rotate(${r.hue}deg)`);
    // Le rayon est en millimètres du cadre, et le cadre mesure boxW mm pour cw px.
    if (r.blur > 0) filtres.push(`blur(${(r.blur / boxW) * cw}px)`);

    let sortie = cvs;
    if (filtres.length) {
        // `ctx.filter` manque à quelques navigateurs anciens : on garde alors ce
        // qu'on a pu cuire (les niveaux, qui passent par la table) plutôt que
        // d'abandonner tout l'export. Même parti pris que l'adoucissement de la
        // sélection de zone.
        const cvs2 = faireCanvas();
        const ctx2 = cvs2.getContext('2d');
        if (typeof ctx2.filter === 'string') {
            ctx2.filter = filtres.join(' ');
            ctx2.drawImage(cvs, 0, 0);
            sortie = cvs2;
        }
    }

    try {
        return sortie.toDataURL('image/png');
    } catch (_) {
        return null;
    }
}

/**
 * Cuit les réglages de toute une liste, juste avant un export.
 *
 * Un calque sans réglage est renvoyé TEL QUEL, sans recompression. Un échec de
 * cuisson laisse le calque intact plutôt que de faire échouer l'export : mieux
 * vaut une image non réglée qu'aucun PDF. Même règle que `flattenEraseMasks`.
 */
export async function aplatirReglages(elements) {
    if (!Array.isArray(elements)) return elements;
    return Promise.all(elements.map(async (el) => {
        if (!aDesReglages(el) || !el.url) return el;
        const cuit = await cuireReglages(el);
        if (!cuit) return el;
        const { adjust, ...reste } = el;
        return { ...reste, url: cuit };
    }));
}
